import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { ExportButton } from '@/components/ui/ExportButton'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { inlineInput } from '@/lib/form-styles'
import { FeldZeile, feldKlasse, leseZahl, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import type { Calculation } from '../types/calculation.types'

interface Gruppe {
  name: string
  wage: number
  share: number
}
type Werte = Record<string, unknown> & { groups: Gruppe[] }
interface Antwort {
  values: Werte
  result: { sharesTotal: number; laborCostRate: number; laborPrice: number; surchargePercent: number; lines: { nr: string; label: string; percent?: number | null; amount: number }[] }
  fromTemplate: boolean
  appliedQualificationCode: string | null
  appliedAt: string | null
  rates: { qualificationCode: string; costRate: number | null; isDefault: boolean }[]
  updatedAt: string | null
}

const ZAHLEN: [string, string, string][] = [
  ['unproductivePercent', '4', 'Unproduktive Zeiten %'],
  ['extraPay', '6', 'Außerkollektivvertragliches Entgelt €/h'],
  ['allowances', '7', 'Zulagen €/h'],
  ['timeSurcharges', '8', 'Arbeitszeitzuschläge €/h'],
  ['taxableExpenses', '9', 'Abgabepflichtige Aufwandsentschädigungen €/h'],
  ['nonTaxable', '11', 'Nicht abgabepflichtige Personalkosten €/h'],
  ['directAncillaryPercent', '12', 'Direkte Personalnebenkosten %'],
  ['allocatedAncillaryPercent', '13', 'Umgelegte Personalnebenkosten %'],
  ['otherAncillary', '14', 'Weitere Personalnebenkosten €/h'],
  ['overheadPercent', '16', 'Personalgemeinkosten %'],
  ['allocations', '17', 'Umlagen €/h'],
]
const prozent = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * AP-37 Detailblatt K 6.2 — **Personalpreis (K3)** im Reiter Sätze & Aufschläge: die
 * Eingaben des Formblatts, gespeichert beim Verlassen des Felds (AP-36), das Ergebnis
 * Zeile für Zeile, Zeile 20 aus dem Zuschlagsschema. „Als Kostensatz übernehmen" mit
 * Vorschau der Angebotssumme; Formblätter K3 und K7 als Datei oder E-Mail; ohne LV
 * „als Ausgabe ablegen".
 */
export function Personalpreis({ kalkulation, onGeaendert }: { kalkulation: Calculation; onGeaendert: () => void }) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const darfVorlage = erfuelltRecht('calculation.settings.manage', user?.permissions)
  const darfAblegen = erfuelltRecht('calculation.calculation.manage', user?.permissions) && !kalkulation.hasLv && !kalkulation.isTemplate
  const url = `/calculations/${kalkulation.id}/personnel-price`
  const [stand, setStand] = useState<Antwort | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const basis = useRef<Werte | null>(null)

  const nimm = useCallback((a: Antwort) => {
    basis.current = a.values
    setStand(a)
    setCode((c) => c || a.appliedQualificationCode || a.rates.find((r) => r.isDefault)?.qualificationCode || a.rates[0]?.qualificationCode || '')
  }, [])
  const laden = useCallback(async () => {
    try {
      nimm((await api.get<{ data: Antwort }>(url)).data.data)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Personalpreis ließ sich nicht laden.'))
    }
  }, [url, nimm])
  useEffect(() => {
    void laden()
  }, [laden, kalkulation.updatedAt])

  const daten = useMemo(() => (stand ? ({ ...stand.values, updatedAt: stand.updatedAt ?? undefined } as Datensatz) : null), [stand])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `calcPersonnelPrice:${kalkulation.id}`,
    bezeichnung: 'Personalpreis (K3)',
    daten,
    // Gespeichert wird das Ganze (PUT): der zuletzt bekannte Stand plus die Änderung.
    schreibe: (patch, st) =>
      api.put<{ data: Antwort }>(url, { values: { ...basis.current, ...patch } }, mitStand(st)).then((r) => {
        nimm(r.data.data)
        return { ...r.data.data.values, updatedAt: r.data.data.updatedAt ?? undefined } as Datensatz
      }),
  }) as Speicherstelle<unknown>

  const gruppen = (stand?.values.groups ?? []) as Gruppe[]
  const gruppeAendern = (liste: (g: Gruppe[]) => Gruppe[]) =>
    void stelle.sende({ kennung: 'groups', felder: ['groups'], labels: { groups: 'Gruppen' }, operation: (aktuell) => ({ groups: liste(((aktuell.groups as Gruppe[]) ?? []).map((g) => ({ ...g }))) }) })

  const uebernehmen = async () => {
    setFehler(null)
    try {
      const v = (await api.post<{ data: { costRateBefore: number | null; costRateAfter: number; priceBefore: number; priceAfter: number } }>(`${url}/apply`, { qualificationCode: code, dryRun: true })).data.data
      const ok = await bestaetige(
        `Kostensatz ${code}: ${v.costRateBefore == null ? '—' : geldText(v.costRateBefore)} → ${geldText(v.costRateAfter)} €/h. Angebotssumme ${geldText(v.priceBefore)} → ${geldText(v.priceAfter)}. Übernehmen?`,
        { ok: 'Übernehmen' },
      )
      if (!ok) return
      await api.post(`${url}/apply`, { qualificationCode: code })
      setHinweis(`Als Kostensatz ${code} übernommen.`)
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Übernahme ist fehlgeschlagen.'))
    }
  }
  const alsVorlage = async () => {
    try {
      await api.post(`${url}/template`)
      setHinweis('Als Vorlage gespeichert — neue Kalkulationen beginnen mit diesen Werten.')
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Vorlage ließ sich nicht speichern.'))
    }
  }
  const ablegen = async (kind: 'K3' | 'K7') => {
    try {
      await api.post(`/calculations/${kalkulation.id}/forms-store`, { kind })
      setHinweis(`Formblatt ${kind} als Ausgabe abgelegt (Reiter Dokumente).`)
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Formblatt ließ sich nicht ablegen.'))
    }
  }

  if (!stand) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  const r = stand.result
  const formblatt = (kind: 'k3' | 'k7') => (
    <ExportButton
      label={`Formblatt ${kind.toUpperCase()}`}
      className="!h-8"
      defaultFilename={`${kind}-${kalkulation.number}`}
      request={(format, orientation) => ({ url: `/calculations/${kalkulation.id}/forms/${kind}`, params: { format, ...(orientation ? { orientation } : {}) } })}
      mail={{ source: 'EXPORT_CALCULATION_FORM', title: t('mailExport.titles.EXPORT_CALCULATION_FORM'), subject: `Formblatt ${kind.toUpperCase()} ${kalkulation.label} ${kalkulation.name}` }}
    />
  )

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Personalpreis (K3)</h3>
          <p className="text-xs text-muted-foreground">
            Herleitung des Kostensatzes nach dem Formblatt K3 (ÖNORM B 2061). Zeile 20 kommt aus dem Zuschlagsschema.
            {stand.fromTemplate && ' Noch keine eigenen Werte — gezeigt wird die Vorlage des Betriebs.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {formblatt('k3')}
          {formblatt('k7')}
          {darfAblegen && (
            <button type="button" className="h-8 rounded-md border border-border px-2.5 text-sm" onClick={() => void ablegen('K7')} title="Ohne LV: das Formblatt K7 als Ausgabe ablegen, z. B. für ein Angebot an einen öffentlichen Auftraggeber">
              K7 ablegen
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <KopfFelder stelle={stelle} darf={darf} />
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>Zeile 1a–1j: Gruppen (Anteile zusammen 100 %)</span>
              <span className={Math.abs(r.sharesTotal - 100) > 0.001 ? 'font-semibold text-destructive' : ''}>Σ {prozent(r.sharesTotal)} %</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {gruppen.map((g, i) => (
                  <tr key={`${stand.updatedAt}-${i}`}>
                    <td className="py-0.5 pr-1">
                      <input className={inlineInput} defaultValue={g.name} disabled={!darf} placeholder="Gruppe" aria-label="Gruppe"
                        onBlur={(e) => e.target.value !== g.name && gruppeAendern((l) => (l[i] && (l[i].name = e.target.value), l))} />
                    </td>
                    <td className="w-28 py-0.5 pr-1">
                      <input className={`${inlineInput} text-right`} inputMode="decimal" defaultValue={g.wage ? String(g.wage).replace('.', ',') : ''} disabled={!darf} placeholder="€/h" aria-label="KV-Entgelt €/h"
                        onBlur={(e) => { const w = leseZahl(e.target.value) ?? 0; if (w !== g.wage) gruppeAendern((l) => (l[i] && (l[i].wage = w), l)) }} />
                    </td>
                    <td className="w-20 py-0.5 pr-1">
                      <input className={`${inlineInput} text-right`} inputMode="decimal" defaultValue={g.share ? String(g.share).replace('.', ',') : ''} disabled={!darf} placeholder="%" aria-label="Anteil %"
                        onBlur={(e) => { const s = leseZahl(e.target.value) ?? 0; if (s !== g.share) gruppeAendern((l) => (l[i] && (l[i].share = s), l)) }} />
                    </td>
                    <td className="w-6">
                      {darf && (
                        <button type="button" className="text-muted-foreground hover:text-destructive" aria-label="Gruppe entfernen" onClick={() => gruppeAendern((l) => l.filter((_, j) => j !== i))}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {darf && gruppen.length < 10 && (
              <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => gruppeAendern((l) => [...l, { name: '', wage: 0, share: 0 }])}>
                <Plus className="h-3.5 w-3.5" /> Gruppe
              </button>
            )}
          </div>
          {ZAHLEN.map(([k, nr, label]) => (
            <ZahlFeld key={k} stelle={stelle} feld={k} label={`${nr} · ${label}`} darf={darf} />
          ))}
        </div>

        <div>
          <table className="w-full text-sm">
            <tbody>
              {r.lines.map((l) => (
                <tr key={l.nr} className={`border-t border-border/50 ${l.nr === '19' || l.nr === '22' ? 'font-semibold' : ''}`}>
                  <td className="w-10 py-1 pr-2 tabular-nums text-muted-foreground">{l.nr}</td>
                  <td className="py-1 pr-2">{l.label}</td>
                  <td className="w-16 py-1 pr-2 text-right tabular-nums text-muted-foreground">{l.percent == null ? '' : `${prozent(l.percent)} %`}</td>
                  <td className="w-24 py-1 text-right tabular-nums">{geldText(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {darf && stand.rates.length > 0 && (
              <>
                <select className={`${inlineInput} !w-40`} value={code} onChange={(e) => setCode(e.target.value)} aria-label="Qualifikation">
                  {stand.rates.map((s) => (
                    <option key={s.qualificationCode} value={s.qualificationCode}>
                      {s.qualificationCode} ({s.costRate == null ? '—' : geldText(s.costRate)})
                    </option>
                  ))}
                </select>
                <button type="button" className="h-8 rounded-md bg-primary px-3 text-primary-foreground disabled:opacity-50" disabled={!code || r.laborCostRate <= 0} onClick={() => void uebernehmen()}>
                  Als Kostensatz übernehmen …
                </button>
              </>
            )}
            {darfVorlage && (
              <button type="button" className="h-8 rounded-md border border-border px-3" onClick={() => void alsVorlage()}>
                Als Vorlage des Betriebs speichern
              </button>
            )}
          </div>
          {stand.appliedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Zuletzt übernommen als {stand.appliedQualificationCode} am {new Date(stand.appliedAt).toLocaleDateString('de-DE')}.
            </p>
          )}
        </div>
      </div>
      {hinweis && <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">{hinweis}</p>}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
    </section>
  )
}

function KopfFelder({ stelle, darf }: { stelle: Speicherstelle<unknown>; darf: boolean }) {
  const lohn = useFeld<string>(stelle, 'wageType', { art: 'auswahl', label: 'Lohn/Gehalt', gesperrt: !darf })
  const arbeit = useFeld<string>(stelle, 'work', { art: 'auswahl', label: 'Art', gesperrt: !darf })
  const kv = useFeld<string | null>(stelle, 'agreement', { label: 'Kollektivvertrag', gesperrt: !darf })
  const datum = useFeld<string | null>(stelle, 'agreementDate', { art: 'datum', label: 'KV gültig ab', gesperrt: !darf })
  const kvStunden = useFeld<number | null>(stelle, 'kvWeeklyHours', { art: 'zahl', stellen: 1, label: 'KV-Wochenarbeitszeit', gesperrt: !darf })
  const stunden = useFeld<number | null>(stelle, 'weeklyHours', { art: 'zahl', stellen: 1, label: 'kalkulierte Wochenarbeitszeit', gesperrt: !darf })
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <FeldZeile label="Lohn/Gehalt" status={lohn}>
        <select className={feldKlasse} {...lohn.props}>
          <option value="LOHN">Lohn</option>
          <option value="GEHALT">Gehalt</option>
        </select>
      </FeldZeile>
      <FeldZeile label="Art" status={arbeit}>
        <select className={feldKlasse} {...arbeit.props}>
          <option value="MONTAGE">Montage</option>
          <option value="VORFERTIGUNG">Vorfertigung</option>
          <option value="REGIE">Regie</option>
        </select>
      </FeldZeile>
      <FeldZeile label="Kollektivvertrag" status={kv}>
        <input className={feldKlasse} {...kv.props} />
      </FeldZeile>
      <FeldZeile label="KV gültig ab" status={datum}>
        <input type="date" className={feldKlasse} {...datum.props} />
      </FeldZeile>
      <FeldZeile label="KV-Wochenarbeitszeit h" status={kvStunden}>
        <input className={feldKlasse} inputMode="decimal" {...kvStunden.props} />
      </FeldZeile>
      <FeldZeile label="kalk. Wochenarbeitszeit h" status={stunden}>
        <input className={feldKlasse} inputMode="decimal" {...stunden.props} />
      </FeldZeile>
    </div>
  )
}

function ZahlFeld({ stelle, feld, label, darf }: { stelle: Speicherstelle<unknown>; feld: string; label: string; darf: boolean }) {
  const f = useFeld<number | null>(stelle, feld, { art: 'zahl', stellen: 2, label, gesperrt: !darf })
  return (
    <FeldZeile label={label} status={f}>
      <input className={`${feldKlasse} text-right`} inputMode="decimal" {...f.props} />
    </FeldZeile>
  )
}
